import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const QuoteForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    customerId: '',
    salespersonId: '',
    expectedServiceDate: '',
    notes: '',
    products: []
  });

  const [customers, setCustomers] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerPriceList, setCustomerPriceList] = useState(null);
  const [draftStatusId, setDraftStatusId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [productForm, setProductForm] = useState({
    productId: '',
    name: '',
    quantity: 1,
    price: 0
  });

  useEffect(() => {
    fetchFormData();
  }, []);

  useEffect(() => {
    if (formData?.customerId) {
      fetchCustomerPriceList(formData?.customerId);
    } else {
      setCustomerPriceList(null);
    }
  }, [formData?.customerId]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch customers
      const { data: customersData, error: customersError } = await supabase
        ?.from('customers')
        ?.select('id, name, email')
        ?.eq('status', 'active')
        ?.order('name');

      if (customersError) throw new Error(`Failed to load customers: ${customersError.message}`);

      setCustomers((customersData || [])?.map(c => ({
        value: c?.id,
        label: c?.name,
        description: c?.email
      })));

      // Fetch salespeople
      const { data: salesData, error: salesError } = await supabase
        ?.from('personnel')
        ?.select('id, name, email')
        ?.eq('role', 'salesperson')
        ?.order('name');

      if (salesError) throw new Error(`Failed to load salespeople: ${salesError.message}`);

      setSalespeople((salesData || [])?.map(s => ({
        value: s?.id,
        label: s?.name,
        description: s?.email
      })));

      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        ?.from('products')
        ?.select('id, name, sku, price')
        ?.order('name');

      if (productsError) throw new Error(`Failed to load products: ${productsError.message}`);

      setProducts((productsData || [])?.map(p => ({
        value: p?.id,
        label: p?.name,
        description: `${p?.sku} - $${p?.price}`,
        price: p?.price
      })));

      // Fetch draft status
      const { data: draftStatus, error: statusError } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Draft')
        ?.single();

      if (statusError) throw new Error(`Failed to load quote status: ${statusError.message}`);

      setDraftStatusId(draftStatus?.id);
    } catch (error) {
      console.error('Error fetching form data:', error?.message);
      setError(error?.message || 'Failed to load form data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerPriceList = async (customerId) => {
    try {
      const { data, error } = await supabase
        ?.from('customer_price_lists')
        ?.select(`
          *,
          price_list_items(*)
        `)
        ?.eq('customer_id', customerId)
        ?.eq('is_active', true)
        ?.or(`valid_until.is.null,valid_until.gte.${new Date()?.toISOString()}`)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      setCustomerPriceList(data || null);
    } catch (error) {
      console.error('Error fetching customer price list:', error?.message);
      setCustomerPriceList(null);
    }
  };

  const getDiscountedPrice = (productId, basePrice) => {
    if (!customerPriceList || !customerPriceList?.price_list_items) return basePrice;

    const priceListItem = customerPriceList?.price_list_items?.find(
      item => item?.product_id === productId
    );

    if (!priceListItem) return basePrice;

    if (priceListItem?.custom_price) {
      return parseFloat(priceListItem?.custom_price);
    }

    if (priceListItem?.discount_type === 'percentage') {
      return basePrice * (1 - priceListItem?.discount_value / 100);
    } else {
      return basePrice - priceListItem?.discount_value;
    }
  };

  const handleProductSelect = (productId) => {
    const selectedProduct = products?.find(p => p?.value === productId);
    if (selectedProduct) {
      const basePrice = selectedProduct?.price || 0;
      const discountedPrice = getDiscountedPrice(productId, basePrice);
      const hasDiscount = discountedPrice < basePrice;

      setProductForm({
        productId: productId,
        name: selectedProduct?.label,
        quantity: 1,
        price: discountedPrice,
        basePrice: basePrice,
        hasDiscount: hasDiscount
      });
    }
  };

  const handleAddProduct = () => {
    if (!productForm?.name || productForm?.quantity <= 0 || productForm?.price < 0) {
      setError('Please fill in all product fields correctly');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setFormData(prev => ({
      ...prev,
      products: [...prev?.products, { ...productForm }]
    }));

    setProductForm({
      productId: '',
      name: '',
      quantity: 1,
      price: 0
    });

    setError(null);
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      products: prev?.products?.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData?.products?.reduce((sum, product) => {
      return sum + (product?.quantity * product?.price);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.customerId || !formData?.salespersonId || formData?.products?.length === 0) {
      setError('Please fill in all required fields and add at least one product');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await onSubmit({
        ...formData,
        statusId: draftStatusId
      });

      // Reset form only on success
      setFormData({
        customerId: '',
        salespersonId: '',
        expectedServiceDate: '',
        notes: '',
        products: []
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      setError(error?.message || 'Failed to create quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-8 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading form...</span>
      </div>
    );
  }

  if (error && loading === false && !isSubmitting) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-8">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
          <div>
            <p className="text-destructive font-medium">Error Loading Form</p>
            <p className="text-sm text-destructive/80 mt-1">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchFormData}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border shadow-elevation-1 p-6">
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Customer"
            placeholder="Select customer"
            required
            searchable
            options={customers}
            value={formData?.customerId}
            onChange={(value) => setFormData(prev => ({ ...prev, customerId: value }))}
          />

          <Select
            label="Salesperson"
            placeholder="Select salesperson"
            required
            searchable
            options={salespeople}
            value={formData?.salespersonId}
            onChange={(value) => setFormData(prev => ({ ...prev, salespersonId: value }))}
          />
        </div>

        <Input
          label="Expected Service Date"
          type="date"
          value={formData?.expectedServiceDate}
          onChange={(e) => setFormData(prev => ({ ...prev, expectedServiceDate: e?.target?.value }))}
          min={new Date()?.toISOString()?.split('T')?.[0]}
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">Notes</label>
          <textarea
            className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y transition-smooth"
            placeholder="Add any notes or special instructions..."
            value={formData?.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
            Products & Services
          </h3>

          <div className="bg-muted/30 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                label="Product"
                placeholder="Select product"
                searchable
                options={products}
                value={productForm?.productId}
                onChange={handleProductSelect}
              />

              <Input
                label="Product/Service Name"
                placeholder="Enter name"
                value={productForm?.name}
                onChange={(e) => setProductForm(prev => ({ ...prev, name: e?.target?.value }))}
              />

              <Input
                label="Quantity"
                type="number"
                min="1"
                value={productForm?.quantity}
                onChange={(e) => setProductForm(prev => ({ ...prev, quantity: parseInt(e?.target?.value) || 1 }))}
              />

              <div>
                <Input
                  label="Price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={productForm?.price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, price: parseFloat(e?.target?.value) || 0 }))}
                />
                {productForm?.hasDiscount && (
                  <p className="text-xs text-success mt-1 flex items-center gap-1">
                    <Icon name="Tag" size={12} />
                    Customer discount applied (Base: ${productForm?.basePrice?.toFixed(2)})
                  </p>
                )}
              </div>
            </div>

            {customerPriceList && (
              <div className="mt-3 bg-success/10 border border-success/20 rounded-lg p-3 flex items-start gap-2">
                <Icon name="Info" size={16} className="text-success mt-0.5" />
                <div className="text-sm text-success">
                  <p className="font-medium">Customer Price List Active: {customerPriceList?.name}</p>
                  <p className="text-xs opacity-80">Special pricing will be automatically applied to selected products</p>
                </div>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddProduct}
              className="mt-4"
            >
              Add Product
            </Button>
          </div>

          {formData?.products?.length > 0 && (
            <div className="bg-background rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Product/Service</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Quantity</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Total</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData?.products?.map((product, index) => (
                    <tr key={index} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-sm text-foreground">{product?.name}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{product?.quantity}</td>
                      <td className="px-4 py-3 text-sm text-foreground">${product?.price?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        ${(product?.quantity * product?.price)?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleRemoveProduct(index)}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/30">
                    <td colSpan="3" className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                      Total Amount:
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-primary">
                      ${calculateTotal()?.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="primary"
            iconName="Save"
            iconPosition="right"
            loading={isSubmitting}
            disabled={!formData?.customerId || !formData?.salespersonId || formData?.products?.length === 0}
          >
            {isSubmitting ? 'Creating Quote...' : 'Create Quote'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default QuoteForm;